// Modal "Cambios del pedido" — lista de overrides activos con opción
// de deshacer cada uno individualmente.
//
// Triggered desde el badge "📝 Modificado" del topbar de empaquetado.
// Resuelve con `true` si se hizo algún cambio (para que el caller
// refresque), `false` si solo se cerró el modal sin tocar nada.

import { apiDel, apiGet } from '../api/client.js';
import { icon } from './icons.js';
import { openAlert } from './modal.js';

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g,
  c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

const fmtNum = (n) => Number.isInteger(n) ? String(n) : String(n);

let _host = null;
function _close() { if (_host) { _host.remove(); _host = null; } }

export function openOverridesModal({ pedidoId }) {
  return new Promise((resolve) => {
    _host = document.createElement('div');
    document.body.appendChild(_host);

    let items = [];
    let cambios = false;  // ¿el operario deshizo algo?

    async function refetch() {
      try {
        items = await apiGet(`/pedidos/${pedidoId}/overrides`);
      } catch (e) {
        if (e?.status === 401) { close(); return; }
        await openAlert({ tipo: 'error', titulo: 'No se pudo cargar', detalle: e?.message || 'Error' });
        return;
      }
      render();
    }

    function close() { _close(); resolve(cambios); }

    function _rowHTML(ov) {
      const ico = ov.accion === 'modificada' ? icon('pencil', { size: 18 })
                : ov.accion === 'añadida'    ? icon('package', { size: 18 })
                                              : icon('trash-2', { size: 18 });
      const color = ov.accion === 'eliminada' ? 'var(--c-error)'
                  : ov.accion === 'añadida'    ? 'var(--c-accent)'
                                                : 'var(--c-partial)';
      const tipo = ov.accion === 'modificada' ? 'Cantidad cambiada'
                 : ov.accion === 'añadida'    ? 'Añadida al pedido'
                                                : 'Quitada del pedido';

      let delta = '';
      if (ov.accion === 'modificada') {
        delta = `<span style="font-weight:600;">${fmtNum(ov.cantidad_original)} → ${fmtNum(ov.cantidad_actual)}</span>`;
      } else if (ov.accion === 'añadida') {
        delta = `<span style="font-weight:600;">+${fmtNum(ov.cantidad_actual)} uds</span>`;
      } else {
        delta = `<span style="font-weight:600;text-decoration:line-through;color:var(--c-fg-muted);">${fmtNum(ov.cantidad_original)} uds</span>`;
      }

      return `
        <div style="
          background: var(--c-surface);
          border: 1px solid var(--c-border);
          border-left: 5px solid ${color};
          border-radius: var(--radius);
          padding: 0.75rem 0.85rem;
          display: flex; gap: 0.6rem; align-items: center;
        ">
          <span style="display:inline-flex;color:${color};">${ico}</span>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;">${escapeHtml(ov.descripcion)}</div>
            <div style="color:var(--c-fg-muted);font-size:var(--fs-xs);text-transform:uppercase;letter-spacing:0.04em;font-weight:600;">${tipo}</div>
            <div style="margin-top:0.25rem;">${delta}</div>
          </div>
          <button class="btn --ghost" data-undo="${ov.override_id}"
                  style="min-height:40px;padding:0 0.7rem;font-size:var(--fs-sm);display:inline-flex;align-items:center;gap:0.3rem;">
            ${icon('rotate-cw', { size: 16 })} Deshacer
          </button>
        </div>`;
    }

    function render() {
      if (!_host) return;
      const cuerpo = items.length === 0
        ? `<div class="empty-state" style="padding:2rem 1rem;">
              <div class="icon" style="display:flex;justify-content:center;">${icon('check-circle', { size: 48 })}</div>
              <p style="margin:0;">No hay cambios — el pedido está como vino del sistema.</p>
            </div>`
        : items.map(_rowHTML).join('');

      _host.innerHTML = `
        <div class="scrim fade-in"><div class="modal" style="max-height:90dvh;overflow-y:auto;">
          <h2>${icon('pencil', { size: 22 })} Cambios del pedido</h2>
          <p class="hint">${items.length} ${items.length === 1 ? 'cambio activo' : 'cambios activos'}. Al cerrar el pedido, el shipment en Dolibarr llevará la lista detallada.</p>
          <div style="display:flex;flex-direction:column;gap:0.5rem;">${cuerpo}</div>
          <div class="actions">
            <button class="btn --primary --block" id="ov-close">Cerrar</button>
          </div>
        </div></div>`;

      _host.querySelector('#ov-close').addEventListener('click', close);
      _host.querySelectorAll('button[data-undo]').forEach(b => {
        b.addEventListener('click', () => deshacer(parseInt(b.dataset.undo, 10)));
      });
    }

    async function deshacer(overrideId) {
      const confirmacion = await openAlert({
        tipo: 'warn',
        titulo: '¿Deshacer este cambio?',
        detalle: 'La línea vuelve al estado original del pedido.',
        accion: 'Deshacer',
        accion2: 'Cancelar',
      });
      if (confirmacion !== 'primary') return;
      try {
        await apiDel(`/pedidos/${pedidoId}/overrides/${overrideId}`);
        cambios = true;
        await refetch();
      } catch (e) {
        if (e?.status === 401) return;
        if (e?.status === 409 && e.detail?.conflicto) {
          const c = e.detail.conflicto;
          await openAlert({
            tipo: 'warn',
            titulo: 'No se puede deshacer',
            detalle: `Hay ${c.ya_empacado} unidades empaquetadas en ${c.cajas_afectadas.length} ${c.cajas_afectadas.length === 1 ? 'caja' : 'cajas'}. Anula esas cajas desde Estructura primero.`,
          });
          return;
        }
        await openAlert({ tipo: 'error', titulo: 'No se pudo deshacer', detalle: e?.message || 'Error' });
      }
    }

    refetch();
  });
}
